import { Dropdown, Input } from "semantic-ui-react";
import Datetime from "react-datetime";
import { parseAsStringLiteral, useQueryState } from "nuqs";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import RuvixxSelect from "components/RuvixxSelect";
import {
  callDispositionOptions,
  callsPerDayOperatorOptions,
  callTypeOptions,
  DATE_FORMAT,
  datePresets,
  dateRangePresets,
  dateRangePresetValues,
  durationOperatorOptions,
} from "@/routes/dashboard/CallDashboardHelpers";
import { useCallDashboardFilters } from "routes/dashboard/useCallDashboardFilters";
import DialSessionService from "services/DialSessions";
import UserService from "services/User";
import CallDispositionService from "services/CallDispositions";
import CampaignService from "services/Campaign";

export function CallDashboardFilters() {
  const [dateRangePreset, setDateRangePreset] = useQueryState(
    "dateRangePreset",
    parseAsStringLiteral(dateRangePresetValues).withDefault("previous7days")
  );
  const [queryFilters, setFilters] = useCallDashboardFilters();

  const handleClearFilters = () => {
    setFilters({
      start_date: datePresets.previous7days.start,
      end_date: datePresets.previous7days.end,
      campaign_ids: [],
      dial_session_ids: [],
      disposition_ids: [],
      agent_ids: [],
      call_type: [],
      duration_operator: null,
      disposition_operator: null,
      calls_per_day_operator: null,
      call_duration: null,
      calls_per_day: null,
    });
    setDateRangePreset("previous7days");
  };

  const handleSelectCampaign = (_, { value }) => {
    setFilters({ campaign_ids: value });
  };
  const handleSelectDialSession = (_, { value }) => {
    setFilters({ dial_session_ids: value });
  };
  const handleSelectDisposition = (_, { value }) => {
    setFilters({ disposition_ids: value });
  };
  const handleSelectAgent = (_, { value }) => {
    setFilters({ agent_ids: value });
  };

  const handleDatePreset = (e, { value }) => {
    setDateRangePreset(value);
    setFilters({
      start_date: datePresets[value].start,
      end_date: datePresets[value].end,
    });
  };

  const handleStartDateChange = moment => {
    setDateRangePreset("");
    setFilters({
      start_date: moment.format ? moment.format(DATE_FORMAT) : "",
    });
  };

  const handleEndDateChange = moment => {
    setDateRangePreset("");
    setFilters({
      end_date: moment.format ? moment.format(DATE_FORMAT) : "",
    });
  };

  const handleSelectCallType = (_, { value }) => {
    setFilters({ call_type: value });
  };

  const handleCallDispositionOperator = (_, { value }) => {
    setFilters({
      disposition_operator: value,
      ...(value ? {} : { disposition_ids: [] }),
    });
  };

  const handleCallDurationOperator = (_, { value }) => {
    setFilters({ duration_operator: value });
  };

  const handleCallDurationChange = (_, { value }) => {
    setFilters({ call_duration: value.replace(/\D/g, "") });
  };

  const handleCallsPerDayOperator = (_, { value }) => {
    setFilters({ calls_per_day_operator: value });
  };

  const handleCallsPerDayChange = (_, { value }) => {
    setFilters({ calls_per_day: value.replace(/\D/g, "") });
  };

  return (
    <Card className="tw:w-full tw:max-w-63 tw:shrink-0">
      <CardHeader>
        <CardTitle>Filters</CardTitle>
        <CardAction>
          <Button variant="destructive" size="sm" onClick={handleClearFilters}>
            clear
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="tw:space-y-4">
        <Dropdown
          className={"filterOption"}
          selection
          placeholder="Select Date Range Preset"
          fluid
          value={dateRangePreset}
          options={dateRangePresets}
          onChange={handleDatePreset}
        />
        <Datetime
          className={"filterOption"}
          closeOnSelect={true}
          dateFormat={"YYYY-MM-DD"}
          timeFormat={false}
          onChange={handleStartDateChange}
          value={queryFilters.start_date}
          renderInput={props => (
            <Input
              placeholder="Start Date"
              icon="calendar outline"
              fluid
              {...props}
            />
          )}
        />
        <Datetime
          className={"filterOption"}
          closeOnSelect={true}
          dateFormat={"YYYY-MM-DD"}
          timeFormat={false}
          onChange={handleEndDateChange}
          value={queryFilters.end_date}
          renderInput={props => (
            <Input
              placeholder="End Date"
              icon="calendar outline"
              fluid
              {...props}
            />
          )}
        />
        <RuvixxSelect
          className={"filterOption"}
          placeholder="Campaign"
          multiple
          clearable
          fluid
          value={queryFilters.campaign_ids}
          queryFn={CampaignService.getCampaignsForFilters}
          onChange={handleSelectCampaign}
        />
        <RuvixxSelect
          className={"filterOption"}
          placeholder="Agent"
          multiple
          clearable
          fluid
          value={queryFilters.agent_ids}
          campaignIds={queryFilters.campaign_ids}
          dialSessionIds={queryFilters.dial_session_ids}
          queryFn={UserService.getCallers}
          onChange={handleSelectAgent}
        />
        <RuvixxSelect
          className={"filterOption"}
          placeholder="Dial Session"
          multiple
          clearable
          fluid
          value={queryFilters.dial_session_ids}
          campaignIds={queryFilters.campaign_ids}
          mapForCampaignId={true}
          queryFn={DialSessionService.getDialSessionsForFilters}
          onChange={handleSelectDialSession}
        />
        <Dropdown
          className={"filterOption"}
          value={queryFilters.call_type}
          clearable
          fluid
          selection
          multiple
          options={callTypeOptions}
          onChange={handleSelectCallType}
          placeholder="Call Type"
        />
        <div className={"filterOption"}>
          <Dropdown
            value={queryFilters.disposition_operator}
            placeholder={"Call Disposition"}
            clearable
            fluid
            selection
            options={callDispositionOptions}
            onChange={handleCallDispositionOperator}
          />
        </div>
        {!!queryFilters.disposition_operator && (
          <RuvixxSelect
            className={"filterOption"}
            placeholder="Select Disposition"
            search
            multiple
            fluid
            clearable
            value={queryFilters.disposition_ids}
            queryFn={CallDispositionService.getDispositionsForFilters}
            onChange={handleSelectDisposition}
          />
        )}
        <div className={"filterOption"}>
          <Dropdown
            value={queryFilters.duration_operator}
            placeholder={"Call Duration"}
            clearable
            fluid
            selection
            options={durationOperatorOptions}
            onChange={handleCallDurationOperator}
          />
        </div>
        {queryFilters.duration_operator && (
          <Input
            fluid
            name="callDuration"
            style={{ marginTop: "1rem" }}
            placeholder="Time in Seconds..."
            value={queryFilters.call_duration}
            onChange={handleCallDurationChange}
          />
        )}
        <div className={"filterOption"}>
          <Dropdown
            value={queryFilters.calls_per_day_operator}
            placeholder="Calls Per Day"
            clearable
            fluid
            selection
            options={callsPerDayOperatorOptions}
            onChange={handleCallsPerDayOperator}
          />
          {queryFilters.calls_per_day_operator && (
            <Input
              fluid
              name="callsPerDay"
              style={{ marginTop: "1rem" }}
              placeholder="Number of Calls..."
              value={queryFilters.calls_per_day}
              onChange={handleCallsPerDayChange}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
